import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

/** historical: "both true, at different times" -- the new sentence took over, the old one is kept as history. */
export type LineReviewStatus = 'pending' | 'approved' | 'rejected' | 'historical';

export interface DiffOp {
  op: 'equal' | 'delete' | 'insert';
  text: string;
}

export interface LineReview {
  id: string;
  documentId: string;
  newDocumentName: string | null;
  newLineId: string;
  newText: string;
  oldLineId: string;
  oldDocumentId: string;
  oldDocumentName: string | null;
  oldText: string;
  similarityPct: number;
  changeType: string | null;
  relationConfidence: number;
  diff: DiffOp[] | null;
  status: LineReviewStatus;
  reviewedBy: string | null;
  reviewedAt: string | null;
  /** Set when another decision in its group (same new line, same old document) closed it. */
  autoRejectedByReviewId: string | null;
  /** Approved with an edit: the reviewer's text that took the old line's place. */
  editedText: string | null;
  /** Both files came in one batch: the "incoming" one is only the one processed later. */
  sameBatch?: boolean;
  /** When each document was last changed (its own modified date, else its source's), if known. */
  newDocumentDate?: string | null;
  newDateSource?: DateSource | null;
  oldDocumentDate?: string | null;
  oldDateSource?: DateSource | null;
  /** Decided without a reviewer -- 'auto': from the documents' dates (the newer one wins);
   *  'ai': by the AI review (tenant in Auto mode). */
  decidedBy?: 'auto' | 'ai' | null;
  autoReason?: string | null;
  createdAt: string;
}

/** Where a document's date came from: the file's own properties, or the system it came from. */
export type DateSource = 'file_metadata' | 'source_modified';

export interface LineReviewList {
  total: number;
  page: number;
  pageSize: number;
  items: LineReview[];
}

/** Waiting reviews per batch of the new documents, counted as Version Review cards. */
export interface PendingByBatch {
  total: number;
  batches: { batchId: string; pending: number }[];
}

/** One sentence in a fact's history: true from its document's date until the next one's. */
export interface TimelineEntry {
  lineId: string;
  text: string;
  documentName: string | null;
  /** null when the document had no date. */
  validFrom: string | null;
  validTo: string | null;
  isCurrent: boolean;
  /** The decision that made this sentence follow the one before it (null for the first). */
  decision: { reviewId: string; reviewedBy: string | null; reviewedAt: string | null; decidedBy: 'auto' | 'ai' | null } | null;
}

/** Sentences linked by "both true, at different times" decisions, oldest first. */
export interface TimelineChain {
  current: string;
  entries: TimelineEntry[];
}

/** Who decides reviews for this tenant. null: not set yet (works as before, set on the server). */
export type VersionControlMode = 'manual' | 'auto';

export interface VersionControlSettings {
  mode: VersionControlMode | null;
  changedBy: string | null;
  changedAt: string | null;
  /** Whether the signed-in user may change the mode (currently everyone). */
  canChange: boolean;
}

export interface LineReviewStats {
  pending: number;
  approved: number;
  rejected: number;
  /** Decided automatically from document dates (also counted in approved/rejected). */
  autoAccepted: number;
}

interface ApiEnvelope<T> {
  statusCode: number;
  success: boolean;
  message: string;
  data: T;
}

/** dpe-backend's api/version-control: human review of line-level updates found across batches. */
@Injectable({ providedIn: 'root' })
export class VersionControl {
  private baseUrl = `${environment.apiUrl}/version-control`;

  constructor(private http: HttpClient) {}

  listReviews(params: { status?: string; documentId?: string; decidedBy?: string; page?: number; pageSize?: number } = {}): Observable<LineReviewList> {
    const query = new URLSearchParams();
    // The backend defaults to pending; an explicit empty value asks for every status.
    query.set('status', params.status ?? '');
    if (params.documentId) query.set('documentId', params.documentId);
    if (params.decidedBy) query.set('decidedBy', params.decidedBy);
    query.set('page', String(params.page ?? 1));
    query.set('pageSize', String(params.pageSize ?? 50));
    return this.http
      .get<ApiEnvelope<LineReviewList>>(`${this.baseUrl}/reviews?${query.toString()}`)
      .pipe(map(res => res.data));
  }

  /** documentId: only the reviews touching that document, as the new or the old side. */
  stats(documentId?: string): Observable<LineReviewStats> {
    const query = documentId ? `?documentId=${encodeURIComponent(documentId)}` : '';
    return this.http.get<ApiEnvelope<LineReviewStats>>(`${this.baseUrl}/reviews/stats${query}`).pipe(map(res => res.data));
  }

  settings(): Observable<VersionControlSettings> {
    return this.http.get<ApiEnvelope<VersionControlSettings>>(`${this.baseUrl}/settings`).pipe(map(res => res.data));
  }

  setMode(mode: VersionControlMode): Observable<VersionControlSettings> {
    return this.http.put<ApiEnvelope<VersionControlSettings>>(`${this.baseUrl}/settings`, { mode }).pipe(map(res => res.data));
  }

  pendingByBatch(): Observable<PendingByBatch> {
    return this.http.get<ApiEnvelope<PendingByBatch>>(`${this.baseUrl}/reviews/pending-by-batch`).pipe(map(res => res.data));
  }

  /** editedText: approve with edit -- that text takes the old line's place instead. */
  approve(id: string, editedText?: string): Observable<LineReview> {
    return this.http
      .post<ApiEnvelope<LineReview>>(`${this.baseUrl}/reviews/${id}/approve`, editedText ? { editedText } : {})
      .pipe(map(res => res.data));
  }

  /** "Both true, at different times": the new sentence takes over, the old one is kept as history. */
  markHistorical(id: string): Observable<LineReview> {
    return this.http.post<ApiEnvelope<LineReview>>(`${this.baseUrl}/reviews/${id}/historical`, {}).pipe(map(res => res.data));
  }

  timeline(): Observable<TimelineChain[]> {
    return this.http.get<ApiEnvelope<{ items: TimelineChain[] }>>(`${this.baseUrl}/timeline`).pipe(map(res => res.data.items));
  }

  reject(id: string): Observable<LineReview> {
    return this.http.post<ApiEnvelope<LineReview>>(`${this.baseUrl}/reviews/${id}/reject`, {}).pipe(map(res => res.data));
  }

  /** "None of these": rejects this pair and the rest of its group. */
  rejectGroup(id: string): Observable<LineReview> {
    return this.http.post<ApiEnvelope<LineReview>>(`${this.baseUrl}/reviews/${id}/reject-group`, {}).pipe(map(res => res.data));
  }

  undo(id: string): Observable<LineReview> {
    return this.http.post<ApiEnvelope<LineReview>>(`${this.baseUrl}/reviews/${id}/undo`, {}).pipe(map(res => res.data));
  }
}
